<template>
  <div
    class="w-full h-full flex items-stretch"
    style="min-height: 764px;"
  >
    <!-- Sidebar Navigation -->
    <div
      class="spendback-sidebar-nav border-r border-border-normal"
      :class="{ 'collapsed': navCollapsed }"
    >
      <div class="nav-body w-full flex flex-col gap-2 p-3 min-w-0">
        <BaseTooltipV2
          v-for="section in sidebarSections"
          :key="`spendback-section-${section.key}`"
          class="w-full h-7"
          :tooltip-enabled="navCollapsed"
          :text="section.name"
          position="right"
          alignment="center"
          :z-index="40001"
        >
          <button
            class="relative w-full h-7 rounded-lg transition-colors duration-100 overflow-hidden"
            :class="section.isActive
              ? 'text-text-muted bg-neutral-25'
              : section.disabled
                ? 'text-text-disabled cursor-not-allowed'
                : 'text-text-normal hover:text-text-muted hover:bg-neutral-25'"
            :disabled="section.disabled"
            @click="changeSection(section.key)"
          >
            <div
              class="absolute top-0 left-0 h-7 flex items-center gap-1.5 p-1"
              style="width: 180px"
            >
              <component
                :is="section.icon"
                class="flex-shrink-0"
              />
              <BaseText
                type="label"
                size="sm"
                class="nav-btn-text flex-shrink-0"
              >
                {{ section.name }}
              </BaseText>
            </div>
          </button>
        </BaseTooltipV2>
      </div>
      <div class="collapse-toggle-container">
        <button
          class="collapse-toggle"
          @click="toggleNavCollapse"
        >
          <ChevronIcon
            class="text-icon-normal transform transition-transform duration-300"
            :style="{ transform: navCollapsed ? 'scaleX(-1) rotate(90deg)' : 'scaleX(1) rotate(90deg)' }"
          />
        </button>
      </div>
      <div
        v-if="!navCollapsed"
        class="nav-footer flex flex-col gap-3 p-3"
      >
        <!-- <div class="flex flex-col gap-2 p-3 rounded-xl bg-white border border-border-normal">
          <div class="flex items-center">
            <div class="support-avatar bg-neutral-200 -mr-1.5" />
            <div class="support-avatar bg-neutral-300" />
          </div>
          <div class="flex flex-col gap-1">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              1:1 Recovery Support
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-text-normal"
            >
              Let us set up your recovery system.
            </BaseText>
          </div>
        </div> -->
        <div class="flex items-center gap-2 p-1">
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal whitespace-nowrap"
          >
            Powered by
          </BaseText>
          <img
            src="../../../assets/images/spendback-logo.png"
            alt="Spendback Logo"
            class="h-2.5 w-auto"
          >
        </div>
      </div>
    </div>

    <!-- Section Content -->
    <div class="flex-grow min-w-0">
      <SpendBackOverviewSection
        v-if="currentSection === 'overview'"
        :lens-id="lensId"
        :accounts="accounts"
      />
      <!-- TODO @sam: build out the remaining sections -->
      <div
        v-else
        class="w-full h-full flex items-center justify-center p-7"
      >
        <BaseText
          type="label"
          size="lg"
          class="text-text-loud"
        >
          {{ currentSectionName }}
        </BaseText>
      </div>
    </div>
  </div>
</template>

<script>
import SpendBackOverviewSection from './dashboard/SpendBackOverviewSection.vue'

// Icons
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import DashboardIcon from '../../globals/Icons/LensBrandIcons/DashboardIcon.vue'
import TableViewIcon from '../../globals/Icons/ViewTypeIcons/TableViewIcon.vue'
import OptimizationIcon from '../../globals/Icons/LensIcons/OptimizationIcon.vue'
import BillingSettingsIcon from '../../globals/Icons/SettingsIcons/BillingSettingsIcon.vue'

const SECTIONS = [
  { name: 'Overview', key: 'overview', icon: DashboardIcon },
  // TODO @sam: enable once these sections are built
  { name: 'Properties', key: 'properties', icon: TableViewIcon, disabled: true },
  { name: 'Insights', key: 'insights', icon: OptimizationIcon, disabled: true },
  { name: 'Settings & Billing', key: 'settings', icon: BillingSettingsIcon, disabled: true }
]
const DEFAULT_SECTION = 'overview'
const NAV_COLLAPSED_KEY = 'spendBackNavCollapsed'

export default {
  name: 'LensSpendBackDashboard',
  components: {
    SpendBackOverviewSection,
    ChevronIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    },
    accounts: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      currentSection: DEFAULT_SECTION,
      navCollapsed: localStorage.getItem(NAV_COLLAPSED_KEY) === 'true'
    }
  },
  computed: {
    sidebarSections () {
      return SECTIONS.map(section => ({
        ...section,
        isActive: this.currentSection === section.key
      }))
    },
    currentSectionName () {
      return SECTIONS.find(section => section.key === this.currentSection)?.name || ''
    }
  },
  watch: {
    // Programmatic navigation onto this page
    '$route.params.section' (section) {
      if (this.isValidSection(section) && section !== this.currentSection) this.currentSection = section
    },
    currentSection () {
      this.syncRoutePath()
    }
  },
  beforeMount () {
    const { section } = this.$route.params
    if (this.isValidSection(section)) this.currentSection = section
    // Reflect the default (or a corrected unknown) section in the path
    if (section !== this.currentSection) this.syncRoutePath()
  },
  methods: {
    isValidSection (section) {
      return SECTIONS.some(({ key, disabled }) => key === section && !disabled)
    },
    changeSection (section) {
      this.currentSection = section
    },
    // Sync the /lens/:lensId/spend-back/:section path without triggering a router reload
    syncRoutePath () {
      history.replaceState({}, null, `/lens/${this.lensId}/spend-back/${this.currentSection}`)
    },
    toggleNavCollapse () {
      this.navCollapsed = !this.navCollapsed
      localStorage.setItem(NAV_COLLAPSED_KEY, String(this.navCollapsed))
    }
  }
}
</script>

<style scoped>
.spendback-sidebar-nav {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 204px;
  flex-shrink: 0;
  background-color: white;
  transition: width 300ms ease-in-out;
}
.spendback-sidebar-nav.collapsed {
  width: 52px;
}
.spendback-sidebar-nav .nav-body {
  position: sticky;
  top: 0px;
}
.spendback-sidebar-nav .nav-btn-text {
  transition: opacity 300ms ease-in-out;
  opacity: 1;
}
.spendback-sidebar-nav.collapsed .nav-btn-text {
  opacity: 0;
}
.spendback-sidebar-nav .nav-footer {
  position: sticky;
  bottom: 0px;
  margin-top: auto;
}
.spendback-sidebar-nav .collapse-toggle-container {
  position: sticky;
  top: calc((100vh - 120px) / 2);
  left: 100%;
  width: 0px;
  height: 0px;
}
.collapse-toggle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid;
  border-color: #ECEFF3;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out;
}
.collapse-toggle:hover {
  background-color: #F6F8FA;
  border-color: #DFE1E6;
}
.support-avatar {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  border: 1px solid white;
}
</style>
